import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import type { AlbumSection } from "@/types";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

function addRange(result: Set<number>, from: number, to: number) {
  for (let n = from; n <= to; n++) result.add(n);
}

function parseSequentialTokens(
  tokens: string[],
  result: Set<number>,
  numberingStart: number,
) {
  for (const token of tokens) {
    const m = /^(\d+)-(\d+)$/.exec(token);
    if (m) {
      // Display range → internal range
      const dispFrom = Number.parseInt(m[1], 10);
      const dispTo = Number.parseInt(m[2], 10);
      addRange(
        result,
        dispFrom - numberingStart + 1,
        dispTo - numberingStart + 1,
      );
    } else {
      const disp = Number.parseInt(token, 10);
      if (!Number.isNaN(disp)) result.add(disp - numberingStart + 1);
    }
  }
}

function addCodedOffset(result: Set<number>, sec: AlbumSection, token: string) {
  const pOff = sec.positionOffset ?? 1;
  const m = /^(\d+)-(\d+)$/.exec(token);
  if (m) {
    const from = sec.start + Number.parseInt(m[1], 10) - pOff;
    const to = sec.start + Number.parseInt(m[2], 10) - pOff;
    for (let seq = from; seq <= Math.min(to, sec.end); seq++) result.add(seq);
  } else {
    const off = Number.parseInt(token, 10);
    if (!Number.isNaN(off)) {
      const seq = sec.start + off - pOff;
      if (seq >= sec.start && seq <= sec.end) result.add(seq);
    }
  }
}

function parseCodedTokens(
  tokens: string[],
  codeMap: Map<string, AlbumSection>,
  result: Set<number>,
  numberingStart: number,
) {
  let i = 0;
  while (i < tokens.length) {
    const sec = codeMap.get(tokens[i].toUpperCase());
    if (sec) {
      i++;
      while (i < tokens.length && !codeMap.has(tokens[i].toUpperCase())) {
        addCodedOffset(result, sec, tokens[i]);
        i++;
      }
    } else {
      // Treat as plain sequential in a coded album (fallback)
      parseSequentialTokens([tokens[i]], result, numberingStart);
      i++;
    }
  }
}

/**
 * Parses a free-form sticker list (from WhatsApp or manual input) into a Set
 * of sequential sticker numbers.
 *
 * Supports both formats transparently:
 *   - Sequential:    "1, 3, 5-8, 10"
 *   - Coded:         "ARG 1-3, ARG 7, ECU 2"  (requires sections with codes)
 *   - Single-line prefix: "Me faltan: ARG 1, ARG 3"  (strips prefix before colon)
 *   - Multi-line Panini:  "RSA 🇿🇦: 4\nKOR 🇰🇷: 13\n..."
 *     Each line `CODE FLAG: n1, n2` is normalised to `CODE n1 n2`.
 *
 * Code lookup is case-insensitive.
 */
export function parseStickerNumbers(
  text: string,
  sections?: AlbumSection[],
  numberingStart = 1,
): Set<number> {
  const result = new Set<number>();

  // Normalise each line: "RSA 🇿🇦: 4, 8" → "RSA 4, 8"
  // For a single-line prefix like "Me faltan: 1, 5" → "Me 1, 5" (non-code "Me" is ignored)
  const normalized = text
    .split(/\r?\n/)
    .map((line) => {
      const colonIdx = line.indexOf(":");
      if (colonIdx < 0) return line;
      const before = line.slice(0, colonIdx).trim();
      const after = line.slice(colonIdx + 1).trim();
      // Keep only the leading alphabetic code (e.g. "RSA", "Me") from before the colon
      const codeMatch = /^([A-Za-z]+)/.exec(before);
      return codeMatch ? `${codeMatch[1]} ${after}` : after;
    })
    .join(" ");

  const hasCodes = sections?.some((s) => s.code);
  const tokens = normalized
    .trim()
    .split(/[\s,]+/)
    .filter(Boolean);

  if (hasCodes) {
    const codeMap = new Map<string, AlbumSection>();
    for (const s of sections ?? []) {
      if (s.code) codeMap.set(s.code.toUpperCase(), s);
    }
    parseCodedTokens(tokens, codeMap, result, numberingStart);
  } else {
    parseSequentialTokens(tokens, result, numberingStart);
  }

  return result;
}

/**
 * Returns the display label for a sticker given the album's sections.
 * - Coded section (code = "ARG"): "ARG 19"
 * - Zero-based (numberingStart = 0): "0", "1", "357"…
 * - Default: raw sequential number as string.
 */
export function stickerLabel(
  num: number,
  sections?: AlbumSection[],
  numberingStart = 1,
): string {
  const section = sections?.find((s) => num >= s.start && num <= s.end);
  if (section?.code) return `${section.code} ${num - section.start + (section.positionOffset ?? 1)}`;
  return String(num - 1 + numberingStart);
}

/** Returns each offset as an individual string (no range compression). */
function compressOffsets(offsets: number[]): string[] {
  return offsets.map(String);
}

/**
 * Builds a human-readable sticker list from an array of sequential numbers.
 * - Default (numberingStart=1, no codes): "1, 2, 3, 5, 8, 12"
 * - Zero-based (numberingStart=0): "0, 1, 2, 4, 7, 11"
 * - Code albums: "ARG 1, ARG 2, ARG 3, ARG 7, ECU 2, ECU 3, ECU 4"
 */
export function stickerRangeList(
  nums: number[],
  sections?: AlbumSection[],
  numberingStart = 1,
): string {
  if (!nums.length) return "\u00a1Ninguna!";
  const sorted = [...nums].sort((a, b) => a - b);
  const hasCodes = sections?.some((s) => s.code);

  if (!hasCodes) {
    // Convert internal numbers to display numbers before compressing
    const displayNums = sorted.map((n) => n - 1 + numberingStart);
    return compressOffsets(displayNums).join(", ");
  }

  // Group by section code, preserving order
  type Group = { prefix: string; offsets: number[] };
  const groups: Group[] = [];
  for (const num of sorted) {
    const sec = sections?.find((s) => num >= s.start && num <= s.end);
    const prefix = sec?.code ?? "";
    const offset = sec ? num - sec.start + (sec.positionOffset ?? 1) : num;
    const last = groups[groups.length - 1];
    if (last?.prefix === prefix) {
      last.offsets.push(offset);
    } else {
      groups.push({ prefix, offsets: [offset] });
    }
  }

  return groups
    .flatMap(({ prefix, offsets }) =>
      compressOffsets(offsets).map((r) => (prefix ? `${prefix} ${r}` : r)),
    )
    .join(", ");
}

export interface ShareMessageParams {
  albumName: string;
  albumSize: number;
  sections?: AlbumSection[];
  numberingStart?: number;
  missingCount: number;
  duplicatesCount: number;
  ownedStickers: Record<number, { owned: boolean; duplicates: number }>;
}

/** Builds a per-section sticker list: one line per section, e.g. "MEX: 1, 3-5\nARG: 2, 8" */
export function buildSectionedList(
  nums: number[],
  sections: AlbumSection[] | undefined,
  numberingStart: number,
): string {
  if (!nums.length) return "\u00a1Ninguna!";
  const sorted = [...nums].sort((a, b) => a - b);
  const hasCodes = sections?.some((s) => s.code);

  if (!hasCodes || !sections) {
    const displayNums = sorted.map((n) => n - 1 + numberingStart);
    return compressOffsets(displayNums).join(", ");
  }

  // Group by section, preserving section order
  const sectionMap = new Map<string, number[]>();
  for (const num of sorted) {
    const sec = sections.find((s) => num >= s.start && num <= s.end);
    const key = sec?.code ?? "";
    if (!sectionMap.has(key)) sectionMap.set(key, []);
    sectionMap.get(key)!.push(sec ? num - sec.start + 1 : num);
  }

  return Array.from(sectionMap.entries())
    .map(([code, offsets]) => {
      const ranges = compressOffsets(offsets).join(", ");
      return code ? `${code}: ${ranges}` : ranges;
    })
    .join("\n");
}

export function buildShareMessage(params: ShareMessageParams): string {
  const {
    albumName,
    albumSize,
    sections,
    numberingStart = 1,
    missingCount,
    duplicatesCount,
    ownedStickers,
  } = params;

  const getNums = (condition: (n: number) => boolean) =>
    Array.from({ length: albumSize }, (_, i) => i + 1).filter(condition);

  const missingText = buildSectionedList(
    getNums((n) => !ownedStickers[n]?.owned),
    sections,
    numberingStart,
  );
  const duplicatesText = buildSectionedList(
    getNums((n) => ownedStickers[n]?.owned && ownedStickers[n]?.duplicates > 0),
    sections,
    numberingStart,
  );

  const lines: string[] = [
    `⚽ *${albumName}* - Intercambio de laminas!`,
    `Organizado con https://mialbum.cl`,
    ``,
    `❌ *Me faltan (${missingCount}):*`,
    missingText,
    ``,
    `✅ *Tengo repetidas (${duplicatesCount}):*`,
    duplicatesText,
    ``,
    `👋 Escribeme para intercambiar y completar nuestro album!`,
  ];
  return lines.join("\n");
}

export interface GroupShareListParams {
  albumName: string;
  sections?: AlbumSection[];
  numberingStart?: number;
  stickers: number[];
}

export function buildGroupMissingMessage(
  params: GroupShareListParams,
): string {
  const { albumName, sections, numberingStart = 1, stickers } = params;
  const list = buildSectionedList(stickers, sections, numberingStart);
  return [
    `⚽ *${albumName}* - Nos faltan estas láminas!`,
    `Organizado con https://mialbum.cl`,
    ``,
    `❌ *Nos faltan (${stickers.length}):*`,
    list,
    ``,
    `👋 ¡Si tienes alguna escríbenos para intercambiar!`,
  ].join("\n");
}

export function buildGroupDuplicatesMessage(
  params: GroupShareListParams,
): string {
  const { albumName, sections, numberingStart = 1, stickers } = params;
  const list = buildSectionedList(stickers, sections, numberingStart);
  return [
    `⚽ *${albumName}* - Tenemos estas láminas repetidas!`,
    `Organizado con https://mialbum.cl`,
    ``,
    `✅ *Nos sobran (${stickers.length}):*`,
    list,
    ``,
    `👋 ¡Si necesitas alguna escríbenos para intercambiar!`,
  ].join("\n");
}
